'use client';

import { useState } from 'react';
import { Button, Card } from 'flowbite-react';
import { HiCheckCircle } from 'react-icons/hi';
import Nav from './Nav';
import CallToActions from './CallToActions';
import Footer from './Footer';
import { FaRocket, FaShieldAlt, FaUserTie, FaLink, FaChartLine, FaDollarSign, FaArrowRight, FaPlus, FaMinus } from 'react-icons/fa';

export default function Page() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Nav />

      <main className="flex-grow">
        {/* Hero Section */}
        <section className="py-16 text-center">
          <div className="px-4 mx-auto max-w-screen-lg">
            <h1 className="mb-4 text-4xl font-extrabold tracking-tight leading-tight lg:text-5xl">
              AI-Powered Link Building for Long-Term SEO Success
            </h1>
            <p className="mb-6 text-lg lg:text-xl">
              Earn high-quality, white-hat backlinks that boost your traffic and rankings. Our AI ensures that every link is earned, credible, and impossible to replicate elsewhere.
            </p>
            <div className="flex justify-center">
              <a href="https://calendly.com/sethmichaelking/15min" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-blue-600 text-white hover:bg-blue-700 flex items-center justify-center">
                <span className="flex items-center">
                  Get Started Today
                  <FaArrowRight className="ml-2" />
                </span>
              </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16">
          <div className="px-4 mx-auto max-w-screen-xl text-center">
            <h2 className="mb-6 text-3xl font-bold text-gray-900">Why Choose Our Link-Building Service?</h2>
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              <Card>
                <FaRocket className="mx-auto mb-4 text-blue-600 text-4xl" />
                <h3 className="text-xl font-bold">AI-Powered Outreach</h3>
                <p>Our AI identifies high-authority platforms where your content can shine, ensuring links that drive traffic and authority.</p>
              </Card>
              <Card>
                <FaShieldAlt className="mx-auto mb-4 text-blue-600 text-4xl" />
                <h3 className="text-xl font-bold">White-Hat Only</h3>
                <p>All links are earned, not bought. This approach enhances your credibility with Google and boosts your rankings.</p>
              </Card>
              <Card>
                <FaUserTie className="mx-auto mb-4 text-blue-600 text-4xl" />
                <h3 className="text-xl font-bold">Dedicated Outreach Manager</h3>
                <p>Work with a personal outreach manager to ensure a smooth process and tailor-made strategies.</p>
              </Card>
              <Card>
                <FaLink className="mx-auto mb-4 text-blue-600 text-4xl" />
                <h3 className="text-xl font-bold">Exclusive Links</h3>
                <p>Earn links that cannot be replicated elsewhere, giving you a competitive edge in search results.</p>
              </Card>
              <Card>
                <FaChartLine className="mx-auto mb-4 text-blue-600 text-4xl" />
                <h3 className="text-xl font-bold">Boost SEO Rankings</h3>
                <p>Drive traffic to your website and improve search rankings through authoritative, contextual backlinks.</p>
              </Card>
              <Card>
                <FaDollarSign className="mx-auto mb-4 text-blue-600 text-4xl" />
                <h3 className="text-xl font-bold">One-Time Payment</h3>
                <p>No recurring fees. Pay once for permanent results starting at just $500 per backlink.</p>
              </Card>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-16">
          <div className="px-4 mx-auto max-w-screen-lg text-center">
            <h2 className="mb-6 text-3xl font-bold text-gray-900">Affordable Pricing</h2>
            <div className="grid gap-8 md:grid-cols-3">
              {[
                {
                  title: "Single Backlink",
                  price: "$500",
                  save: "Best Value",
                  features: [
                    "High-quality, DR 50+ links",
                    "Guaranteed placement",
                    "Delivery estimate: 1-30 days",
                    "One-time payment",
                  ],
                },
                {
                  title: "10 Backlinks",
                  price: "$4,500",
                  save: "Save $500",
                  features: [
                    "Bulk discount",
                    "Guaranteed placements",
                    "Delivery estimate: 30–70 days",
                    "One-time payment",
                  ],
                },
                {
                  title: "20 Backlinks",
                  price: "$8,000",
                  save: "Save $2,000",
                  features: [
                    "Exclusive pricing",
                    "Permanent links",
                    "Delivery estimate: 70+ days",
                    "One-time payment",
                  ],
                },
              ].map(({ title, price, save, features }, index) => (
                <Card key={index} className="flex flex-col justify-between h-full">
                  <div className="flex-grow">
                    <h3 className="mb-2 text-xl font-bold">{title}</h3>
                    <p className="text-2xl font-extrabold">{price}</p>
                    {save && <p className="text-sm text-gray-500">{save}</p>}
                    <ul className="my-4 text-left space-y-2">
                      {features.map((feature, i) => (
                        <li key={i} className="flex items-center">
                          <HiCheckCircle className="mr-2 text-green-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <a href="https://calendly.com/sethmichaelking/15min" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-blue-600 text-white hover:bg-blue-700 flex items-center justify-center">
  <span className="flex items-center">
   Order Now
    <FaArrowRight className="ml-2" />
  </span>
</Button>
                  </a>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section with Accordion */}
        <section className="py-16 bg-white">
          <div className="px-4 mx-auto max-w-screen-lg">
            <h2 className="mb-6 text-3xl font-bold text-gray-900 text-center">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {[
                {
                  question: 'How does the link-building process work?',
                  answer:
                    'We work with clients from any industry who need media coverage. Our tool identifies PR opportunities, and we leverage our extensive network to find the best placements for your content. Journalists often work on tight deadlines, so we do our best to secure relevant coverage, but the timing of when the link goes live is beyond our control. Journalists may sometimes have their stories cancelled or delayed, and they may not always know when the article will go live.',
                },
                {
                  question: 'Are the links permanent?',
                  answer:
                    'Yes, all links are permanent, and we guarantee placement for your content. However, since journalists sometimes experience changes in their editorial schedules, we cannot guarantee a specific timeframe for when the link will be published.',
                },
                {
                  question: 'Can I choose where the links go?',
                  answer:
                    'We provide recommendations on where the link will be placed based on relevance to your industry and the target audience. However, the final decision on the destination URL rests with you. Keep in mind, the approval process is not always immediate, as journalists rarely let sources view their quotes before publication.',
                },
                {
                  question: 'Will I be able to approve the article before it goes live?',
                  answer:
                    'Unfortunately, journalists rarely allow sources to review their quotes before an article is published. While we strive to get the best placements for your content, we cannot guarantee approval of the story prior to its release.',
                },
              ].map((item, index) => (
                <div key={index} className="border-b">
                  <button
                    onClick={() => toggleAccordion(index)}
                    className="w-full text-left p-4 text-lg font-semibold text-gray-900 focus:outline-none hover:bg-gray-100 flex justify-between items-center"
                  >
                    <span>{item.question}</span>
                    <span className="text-xl">
                      {openIndex === index ? (
                        <FaMinus />
                      ) : (
                        <FaPlus />
                      )}
                    </span>
                  </button>
                  {openIndex === index && (
                    <div className="p-4 text-gray-700">
                      <p>{item.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <CallToActions />
      <Footer />
    </div>
  );
}
